import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import VideoDetectionList from "../../VideoDetectionList";
import PatientsList from "../PatientsList/PatientsList";
import ProfileDetail from "../ProfileDetail/ProfileDetail";
import DoctorList from "../DoctorList/DoctorList";
import RAList from "../RAList/RAList";
import LabelerList from "../LabelerList/LabelerList";
import InstitutionOwnerList from "../InstitutionOwnerList/InstitutionOwnerList";
import AdminList from "../AdminList/AdminList";
import UserProfileDetail from "../UserProfileDetail/UserProfileDetail";
import { fetchVideoList, fetchUserProfile, fetchPatientList } from "../../../api";
import { USER_ROLES } from "../../constants";
import { MdSlowMotionVideo } from "react-icons/md";
import { ImFilePlay } from "react-icons/im";
import { RiFileCheckLine } from "react-icons/ri";
import { RiFileCloseLine } from "react-icons/ri";
import { FaRegUser } from "react-icons/fa";
import { AiOutlineHome } from "react-icons/ai";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { AiOutlineMail } from "react-icons/ai";
import { FaArrowLeft } from "react-icons/fa";
import Cookies from "js-cookie";
import { default as DropdownMenu } from "@mui/material/Menu";
import { default as DropdownMenuItem } from "@mui/material/MenuItem";
import "./index.css";
import hkuLogo from "../../../assets/hku-logo.svg";

// Updated menu items
const MENU_ITEMS = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: AiOutlineHome,
    type: "component",
  },
  {
    id: "video",
    label: "Video Labeling List",
    icon: MdSlowMotionVideo,
    type: "component",
  },
  {
    id: "participants",
    label: "Participants List",
    icon: FaRegUser,
    type: "component",
  },
  {
    id: "doctors",
    label: "Doctor List",
    icon: FaRegUser,
    type: "component",
  },
  {
    id: "ras",
    label: "RA List",
    icon: FaRegUser,
    type: "component",
  },
  {
    id: "labelers",
    label: "Labeler List",
    icon: FaRegUser,
    type: "component",
  },
  {
    id: "institutionowners",
    label: "Institution Owner List",
    icon: FaRegUser,
    type: "component",
  },
  {
    id: "admins",
    label: "Admin List",
    icon: FaRegUser,
    type: "component",
  },
  {
    id: "disclaimer",
    label: "Disclaimer",
    icon: AiOutlineInfoCircle,
    type: "component",
  },
  {
    id: "contact",
    label: "Contact Info",
    icon: AiOutlineMail,
    type: "component",
  },
];

// Create screen components
const DashboardHome = () => {
  const [videos, setVideos] = useState(undefined);       // undefined = loading, null = failed
  const [participantCount, setParticipantCount] = useState(undefined);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    const [videoResult, patientResult] = await Promise.allSettled([
      fetchVideoList(),
      fetchPatientList(),
    ]);

    if (videoResult.status === "fulfilled") {
      setVideos(videoResult.value);
    } else {
      console.error("Error loading videos:", videoResult.reason);
      setVideos(null); // null signals failed
    }

    if (patientResult.status === "fulfilled") {
      setParticipantCount(patientResult.value?.patients?.length ?? 0);
    } else {
      console.error("Error loading participants:", patientResult.reason);
      setParticipantCount(null); // null signals failed
    }

    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const getVideoStats = () => {
    if (!videos) return null;
    const labeledCount = videos.labeled_videos?.length || 0;
    const unlabeledCount = videos.non_labeled_videos?.length || 0;
    return {
      total: labeledCount + unlabeledCount,
      labeled: labeledCount,
      unlabeled: unlabeledCount,
    };
  };

  const videoStats = getVideoStats();

  const ALL_STATS = [
    {
      id: "all-video",
      value: loading ? "..." : videoStats?.total.toString(),
      label: "All Videos",
      icon: ImFilePlay,
      type: "component",
      bgClass: "view5",
      available: loading || videoStats !== null,
    },
    {
      id: "labeled-video",
      value: loading ? "..." : videoStats?.labeled.toString(),
      label: "Labeled Videos",
      icon: RiFileCheckLine,
      type: "component",
      bgClass: "view6",
      available: loading || videoStats !== null,
    },
    {
      id: "unlabeled-video",
      value: loading ? "..." : videoStats?.unlabeled.toString(),
      label: "Unlabeled Videos",
      icon: RiFileCloseLine,
      type: "component",
      bgClass: "view5",
      available: loading || videoStats !== null,
    },
    {
      id: "users",
      value: loading ? "..." : participantCount?.toString(),
      label: "Participants",
      icon: FaRegUser,
      type: "component",
      bgClass: "view5",
      available: loading || participantCount !== null,
    },
  ];

  const visibleStats = ALL_STATS.filter((s) => s.available);

  if (loading) {
    return (
      <div className="dashboard-section">
        <div className="section-header">
          <h1 className="section-title">Dashboard</h1>
        </div>
        <div className="stats-section">
          <div className="loading-message">Loading dashboard data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-section">
      <div className="section-header">
        <h1 className="section-title">Dashboard</h1>
      </div>
      <div className="stats-section">
        {visibleStats.length > 0 ? (
          <div className="stats-grid">
            {visibleStats.map((stat) => (
              <StatCard key={stat.id} stat={stat} />
            ))}
          </div>
        ) : (
          <div className="error-message">
            <p>Unable to load dashboard data.</p>
            <button onClick={loadData}>Retry</button>
          </div>
        )}
      </div>
    </div>
  );
};

const DisclaimerScreen = () => (
  <div className="dashboard-section">
    <div className="section-header">
      <h1 className="section-title">Disclaimer</h1>
    </div>
    <div className="info-content">
      <div className="info-card">
        <p className="info-paragraph">
          <strong>Important:</strong> AI-ASD123 is a health information management tool that uses AI to process, summarise and organise health-related information. It does not provide medical advice, diagnosis, or treatment, and its outputs may be incomplete or inaccurate. Do not use AI-ASD123 to make medical decisions or replace a qualified healthcare professional's judgment.
        </p>
        <p className="info-paragraph">
          This report is for reference only. If you have concerns regarding your child's development or cognitive function, please contact your clinician in charge.
        </p>
        <p className="info-paragraph">
          Development in children is a continuous process. As a child grows and develops, different aspects of him or her are changing continuously. Therefore, the information and recommendations contained in this report are meant for current use. Reference to or use of this report in the future should be made with considerable caution and reservation.
        </p>
      </div>
    </div>
  </div>
);

const ContactScreen = () => (
  <div className="dashboard-section">
    <div className="section-header">
      <h1 className="section-title">Contact Info</h1>
    </div>
    <div className="info-content">
      <div className="info-card">
        <div className="contact-item">
          <strong>Email:</strong>
          <a href="mailto:lovebb@hku.hk" className="contact-link">
            lovebb@hku.hk
          </a>
        </div>
        <div className="contact-item">
          <strong>WhatsApp:</strong>
          <a href="https://wa.me/85260854678" className="contact-link">
            6085 4678
          </a>
        </div>
      </div>
    </div>
  </div>
);

const VideoScreen = () => (
  <div className="dashboard-section">
    <div className="section-header">
      <h1 className="section-title">Video Labeling List</h1>
    </div>
    <VideoDetectionList />
  </div>
);

const PatientsScreen = () => {
  const navigate = useNavigate();

  const handleNewPatientClick = () => {
    navigate("/patient/new");
  };

  const handlePatientClick = (patient) => {
    navigate(`/patient/${patient.patient_id}`);
  };

  return (
    <div className="dashboard-section">
      <div className="section-header-with-action">
        <h1 className="section-title">Participants List</h1>
        <div className="new-invoice-button" onClick={handleNewPatientClick}>
          <span className="new-invoice-text">New Participant</span>
        </div>
      </div>
      <PatientsList onPatientClick={handlePatientClick} />
    </div>
  );
};

const DoctorsScreen = () => {
  const navigate = useNavigate();

  const handleNewDoctorClick = () => {
    navigate("/doctor/new");
  };

  const handleDoctorClick = (doctor) => {
    navigate(`/doctor/${doctor.username}`);
  };

  return (
    <div className="dashboard-section">
      <div className="section-header-with-action">
        <h1 className="section-title">Doctor List</h1>
        <div className="new-invoice-button" onClick={handleNewDoctorClick}>
          <span className="new-invoice-text">New Doctor</span>
        </div>
      </div>
      <DoctorList onDoctorClick={handleDoctorClick} />
    </div>
  );
};

const RAsScreen = () => {
  const navigate = useNavigate();

  const handleNewRAClick = () => {
    navigate("/ra/new");
  };

  const handleRAClick = (ra) => {
    navigate(`/ra/${ra.username}`);
  };

  return (
    <div className="dashboard-section">
      <div className="section-header-with-action">
        <h1 className="section-title">RA List</h1>
        <div className="new-invoice-button" onClick={handleNewRAClick}>
          <span className="new-invoice-text">New RA</span>
        </div>
      </div>
      <RAList onRAClick={handleRAClick} />
    </div>
  );
};

const LabelersScreen = () => {
  const navigate = useNavigate();

  const handleNewLabelerClick = () => {
    navigate("/labeler/new");
  };

  const handleLabelerClick = (labeler) => {
    navigate(`/labeler/${labeler.username}`);
  };

  return (
    <div className="dashboard-section">
      <div className="section-header-with-action">
        <h1 className="section-title">Labeler List</h1>
        <div className="new-invoice-button" onClick={handleNewLabelerClick}>
          <span className="new-invoice-text">New Labeler</span>
        </div>
      </div>
      <LabelerList onLabelerClick={handleLabelerClick} />
    </div>
  );
};

const InstitutionOwnersScreen = () => {
  const navigate = useNavigate();

  const handleNewInstitutionOwnerClick = () => {
    navigate("/institutionowner/new");
  };

  const handleInstitutionOwnerClick = (institutionOwner) => {
    navigate(`/institutionowner/${institutionOwner.username}`);
  };

  return (
    <div className="dashboard-section">
      <div className="section-header-with-action">
        <h1 className="section-title">Institution Owner List</h1>
        <div className="new-invoice-button" onClick={handleNewInstitutionOwnerClick}>
          <span className="new-invoice-text">New Institution Owner</span>
        </div>
      </div>
      <InstitutionOwnerList onInstitutionOwnerClick={handleInstitutionOwnerClick} />
    </div>
  );
};

const AdminsScreen = () => {
  const navigate = useNavigate();

  const handleNewAdminClick = () => {
    navigate("/admin/new");
  };

  const handleAdminClick = (admin) => {
    navigate(`/admin/${admin.username}`);
  };

  return (
    <div className="dashboard-section">
      <div className="section-header-with-action">
        <h1 className="section-title">Admin List</h1>
        <div className="new-invoice-button" onClick={handleNewAdminClick}>
          <span className="new-invoice-text">New Admin</span>
        </div>
      </div>
      <AdminList onAdminClick={handleAdminClick} />
    </div>
  );
};

const PatientFormScreen = ({ screenData }) => {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const location = useLocation();

  // Determine mode based on screenData prop or URL
  const mode = screenData?.mode || (patientId === "new" ? "create" : "edit");
  const actualPatientId = patientId === "new" ? null : patientId;

  const handleBackClick = () => {
    navigate("/patients");
  };

  const handlePatientSave = (savedPatientData) => {
    console.log("Participant saved:", savedPatientData);
    navigate("/patients");
  };

  const handlePatientCancel = () => {
    navigate("/patients");
  };

  const handlePatientDelete = () => {
    navigate("/patients");
  };

  return (
    <div className="dashboard-section">
      <div className="section-header-with-back">
        <div className="back-button" onClick={handleBackClick}>
          <FaArrowLeft className="back-icon" />
          <span className="back-text">Back to Participants</span>
        </div>
        <h1 className="section-title">
          {mode === "create" ? "Create New Participant" : "Edit Participant"}
        </h1>
      </div>
      <div className="patient-form-content">
        <ProfileDetail
          mode={mode}
          patientId={actualPatientId}
          onSave={handlePatientSave}
          onCancel={handlePatientCancel}
          onDelete={handlePatientDelete}
        />
      </div>
    </div>
  );
};

const DoctorFormScreen = ({ screenData }) => {
  const navigate = useNavigate();
  const { doctorId } = useParams();
  const location = useLocation();

  const mode = screenData?.mode || (doctorId === "new" ? "create" : "edit");
  const actualDoctorId = doctorId === "new" ? null : doctorId;

  const handleBackClick = () => {
    navigate("/doctors");
  };

  const handleDoctorSave = (savedDoctorData) => {
    console.log("Doctor saved:", savedDoctorData);
    navigate("/doctors");
  };

  const handleDoctorCancel = () => {
    navigate("/doctors");
  };

  const handleDoctorDelete = () => {
    navigate("/doctors");
  };

  return (
    <div className="dashboard-section">
      <div className="section-header-with-back">
        <div className="back-button" onClick={handleBackClick}>
          <FaArrowLeft className="back-icon" />
          <span className="back-text">Back to Doctors</span>
        </div>
        <h1 className="section-title">
          {mode === "create" ? "Create New Doctor" : "Edit Doctor"}
        </h1>
      </div>
      <div className="patient-form-content">
        <UserProfileDetail
          mode={mode}
          userId={actualDoctorId}
          userType="doctor"
          onSave={handleDoctorSave}
          onCancel={handleDoctorCancel}
          onDelete={handleDoctorDelete}
        />
      </div>
    </div>
  );
};

const RAFormScreen = ({ screenData, userProfile }) => {
  const navigate = useNavigate();
  const { raId } = useParams();
  const location = useLocation();

  const mode = screenData?.mode || (raId === "new" ? "create" : "edit");
  const actualRAId = raId === "new" ? null : raId;

  const handleBackClick = () => {
    navigate("/ras");
  };

  const handleRASave = (savedRAData) => {
    console.log("RA saved:", savedRAData);
    navigate("/ras");
  };

  const handleRACancel = () => {
    navigate("/ras");
  };

  const handleRADelete = () => {
    navigate("/ras");
  };

  return (
    <div className="dashboard-section">
      <div className="section-header-with-back">
        <div className="back-button" onClick={handleBackClick}>
          <FaArrowLeft className="back-icon" />
          <span className="back-text">Back to RAs</span>
        </div>
        <h1 className="section-title">
          {mode === "create" ? "Create New RA" : "Edit RA"}
        </h1>
      </div>
      <div className="patient-form-content">
        <UserProfileDetail
          mode={mode}
          userId={actualRAId}
          userType="ra"
          onSave={handleRASave}
          onCancel={handleRACancel}
          onDelete={handleRADelete}
          currentUserProfile={userProfile}
        />
      </div>
    </div>
  );
};

const LabelerFormScreen = ({ screenData }) => {
  const navigate = useNavigate();
  const { labelerId } = useParams();
  const location = useLocation();

  const mode = screenData?.mode || (labelerId === "new" ? "create" : "edit");
  const actualLabelerId = labelerId === "new" ? null : labelerId;

  const handleBackClick = () => {
    navigate("/labelers");
  };

  const handleLabelerSave = (savedLabelerData) => {
    console.log("Labeler saved:", savedLabelerData);
    navigate("/labelers");
  };

  const handleLabelerCancel = () => {
    navigate("/labelers");
  };

  const handleLabelerDelete = () => {
    navigate("/labelers");
  };

  return (
    <div className="dashboard-section">
      <div className="section-header-with-back">
        <div className="back-button" onClick={handleBackClick}>
          <FaArrowLeft className="back-icon" />
          <span className="back-text">Back to Labelers</span>
        </div>
        <h1 className="section-title">
          {mode === "create" ? "Create New Labeler" : "Edit Labeler"}
        </h1>
      </div>
      <div className="patient-form-content">
        <UserProfileDetail
          mode={mode}
          userId={actualLabelerId}
          userType="labeler"
          onSave={handleLabelerSave}
          onCancel={handleLabelerCancel}
          onDelete={handleLabelerDelete}
        />
      </div>
    </div>
  );
};

const InstitutionOwnerFormScreen = ({ screenData }) => {
  const navigate = useNavigate();
  const { institutionOwnerId } = useParams();
  const location = useLocation();

  const mode = screenData?.mode || (institutionOwnerId === "new" ? "create" : "edit");
  const actualInstitutionOwnerId = institutionOwnerId === "new" ? null : institutionOwnerId;

  const handleBackClick = () => {
    navigate("/institutionowners");
  };

  const handleInstitutionOwnerSave = (savedInstitutionOwnerData) => {
    console.log("Institution Owner saved:", savedInstitutionOwnerData);
    navigate("/institutionowners");
  };

  const handleInstitutionOwnerCancel = () => {
    navigate("/institutionowners");
  };

  const handleInstitutionOwnerDelete = () => {
    navigate("/institutionowners");
  };

  return (
    <div className="dashboard-section">
      <div className="section-header-with-back">
        <div className="back-button" onClick={handleBackClick}>
          <FaArrowLeft className="back-icon" />
          <span className="back-text">Back to Institution Owners</span>
        </div>
        <h1 className="section-title">
          {mode === "create" ? "Create New Institution Owner" : "Edit Institution Owner"}
        </h1>
      </div>
      <div className="patient-form-content">
        <UserProfileDetail
          mode={mode}
          userId={actualInstitutionOwnerId}
          userType="institutionowner"
          onSave={handleInstitutionOwnerSave}
          onCancel={handleInstitutionOwnerCancel}
          onDelete={handleInstitutionOwnerDelete}
        />
      </div>
    </div>
  );
};

const AdminFormScreen = ({ screenData }) => {
  const navigate = useNavigate();
  const { adminId } = useParams();
  const location = useLocation();

  const mode = screenData?.mode || (adminId === "new" ? "create" : "edit");
  const actualAdminId = adminId === "new" ? null : adminId;

  const handleBackClick = () => {
    navigate("/admins");
  };

  const handleAdminSave = (savedAdminData) => {
    console.log("Admin saved:", savedAdminData);
    navigate("/admins");
  };

  const handleAdminCancel = () => {
    navigate("/admins");
  };

  const handleAdminDelete = () => {
    navigate("/admins");
  };

  return (
    <div className="dashboard-section">
      <div className="section-header-with-back">
        <div className="back-button" onClick={handleBackClick}>
          <FaArrowLeft className="back-icon" />
          <span className="back-text">Back to Admins</span>
        </div>
        <h1 className="section-title">
          {mode === "create" ? "Create New Admin" : "Edit Admin"}
        </h1>
      </div>
      <div className="patient-form-content">
        <UserProfileDetail
          mode={mode}
          userId={actualAdminId}
          userType="admin"
          onSave={handleAdminSave}
          onCancel={handleAdminCancel}
          onDelete={handleAdminDelete}
        />
      </div>
    </div>
  );
};

// Keep existing components (MenuItem, StatCard, etc.)
const MenuItem = ({ item, isActive, onClick }) => {
  const renderIcon = () => {
    if (item.type === "component") {
      const IconComponent = item.icon;
      return <IconComponent className="menu-icon" />;
    } else {
      return <img src={item.icon} alt={item.label} className="menu-icon" />;
    }
  };

  return (
    <div
      className={`menu-item ${isActive ? "menu-item--active" : ""}`}
      onClick={() => onClick(item.id)}
    >
      {renderIcon()}
      <span className="menu-text">{item.label}</span>
      {isActive && <div className="active-indicator"></div>}
    </div>
  );
};

const StatCard = ({ stat }) => {
  const renderIcon = () => {
    if (stat.type === "component") {
      const IconComponent = stat.icon;
      return stat.bgClass ? (
        <div className={`stat-icon-container ${stat.bgClass}`}>
          <IconComponent className="stat-icon" />
        </div>
      ) : (
        <IconComponent className="stat-icon-large" />
      );
    } else {
      return stat.bgClass ? (
        <div className={`stat-icon-container ${stat.bgClass}`}>
          <img src={stat.icon} alt={stat.label} className="stat-icon" />
        </div>
      ) : (
        <img src={stat.icon} alt={stat.label} className="stat-icon-large" />
      );
    }
  };

  return (
    <div className="stat-card">
      {renderIcon()}
      <div className="stat-content">
        <span className="stat-value">{stat.value}</span>
        <span className="stat-label">{stat.label}</span>
      </div>
    </div>
  );
};

function getVisibleMenuItems(role) {
  if (role === USER_ROLES.ADMIN || role === USER_ROLES.ITDOG) return MENU_ITEMS;
  
  if (role === USER_ROLES.LABELER)
    return MENU_ITEMS.filter((item) => 
      ["dashboard", "video", "disclaimer", "contact"].includes(item.id)
    );
  
  if (role === USER_ROLES.DOCTOR)
    return MENU_ITEMS.filter((item) => 
      ["dashboard", "disclaimer", "contact", "participants", "ras"].includes(item.id)
    );
  
  if (role === USER_ROLES.RA)
    return MENU_ITEMS.filter((item) => 
      ["dashboard", "disclaimer", "contact", "participants"].includes(item.id)
    );
  
  if (role === USER_ROLES.INSTITUTION_OWNER)
    return MENU_ITEMS.filter((item) => 
      ["dashboard", "disclaimer", "contact", "participants", "ras"].includes(item.id)
    );
  
  return [];
}

const Sidebar = ({
  activeMenuItem,
  onMenuItemClick,
  userProfile,
  sidebarOpen,
  setSidebarOpen,
}) => (
  <>
    <aside className={`sidebar${sidebarOpen ? " sidebar--open" : ""}`}>
      <header className="sidebar-header">
        <img src={hkuLogo} alt="Logo" className="logo" />
      </header>
      <nav className="sidebar-nav">
        {getVisibleMenuItems(userProfile?.platform_role).map((item) => (
          <MenuItem
            key={item.id}
            item={item}
            isActive={activeMenuItem === item.id}
            onClick={(menuId) => {
              onMenuItemClick(menuId);
              setSidebarOpen(false); // close sidebar on mobile after click
            }}
          />
        ))}
      </nav>
    </aside>
    {/* Overlay for mobile */}
    {sidebarOpen && (
      <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
    )}
  </>
);

const Header = ({ userProfile, userLoading, setSidebarOpen }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = () => {
    Cookies.remove("platform_role");
    Cookies.remove("access_token");
    setAnchorEl(null);
    window.location.href = "/";
  };

  return (
    <header className="main-header">
      <button
        className="burger-menu"
        onClick={() => setSidebarOpen((open) => !open)}
        aria-label="Open sidebar"
      >
        <span className="burger-bar"></span>
        <span className="burger-bar"></span>
        <span className="burger-bar"></span>
      </button>
      <div className="search-section" style={{ visibility: "hidden" }}>
        <img
          src="https://storage.googleapis.com/tagjs-prod.appspot.com/v1/pP5ulGK3xn/tw93ixce_expires_30_days.png"
          alt="Search"
          className="search-icon"
        />
        <span className="search-placeholder">Search here...</span>
      </div>

      <div className="user-section">
        <div className="user-info">
          <div className="user-details">
            <span className="user-name">
              {userLoading
                ? "Loading..."
                : userProfile?.full_name || userProfile?.username || "User"}
            </span>
            <span className="user-role">
              {userLoading ? "..." : userProfile?.platform_role || "User"}
            </span>
          </div>
          <img
            src="https://www.shutterstock.com/image-vector/user-profile-icon-vector-avatar-600nw-2247726673.jpg"
            alt="User avatar"
            className="user-avatar"
            style={{ cursor: "pointer" }}
            onClick={handleAvatarClick}
            aria-controls={open ? "avatar-menu" : undefined}
          />
          <DropdownMenu
            id="avatar-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "avatar-button",
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <DropdownMenuItem onClick={handleSignOut}>Sign Out</DropdownMenuItem>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

const Dashboard = ({ activeScreen = "dashboard", screenData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userProfile, setUserProfile] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [userError, setUserError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Determine active menu item based on current route
  const getActiveMenuItem = () => {
    const path = location.pathname;
    if (path === "/dashboard") return "dashboard";
    if (path === "/video") return "video";
    if (path === "/patients") return "participants";
    if (path.startsWith("/patient/")) return "participants"; // Keep patients menu active for patient forms
    if (path === "/doctors") return "doctors";
    if (path.startsWith("/doctor/")) return "doctors"; // Keep doctors menu active for doctor forms
    if (path === "/ras") return "ras";
    if (path.startsWith("/ra/")) return "ras"; // Keep ras menu active for ra forms
    if (path === "/labelers") return "labelers";
    if (path.startsWith("/labeler/")) return "labelers"; // Keep labelers menu active for labeler forms
    if (path === "/institutionowners") return "institutionowners";
    if (path.startsWith("/institutionowner/")) return "institutionowners"; // Keep institution owners menu active
    if (path === "/admins") return "admins";
    if (path.startsWith("/admin/")) return "admins"; // Keep admins menu active for admin forms
    if (path === "/disclaimer") return "disclaimer";
    if (path === "/contact") return "contact";
    return "dashboard";
  };

  // Load user profile on component mount
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        setUserLoading(true);
        setUserError(null);
        const profileData = await fetchUserProfile();
        setUserProfile(profileData);
        Cookies.set("platform_role", profileData.platform_role);
      } catch (error) {
        console.error("Error loading user profile:", error);
        setUserError("Failed to load user profile");
        setUserProfile({
          full_name: "User",
          username: "user",
          platform_role: "User",
        });
      } finally {
        setUserLoading(false);
      }
    };

    loadUserProfile();
  }, []);

  const handleMenuItemClick = (menuId) => {
    switch (menuId) {
      case "dashboard":
        navigate("/dashboard");
        break;
      case "video":
        navigate("/video");
        break;
      case "participants":
        navigate("/patients");
        break;
      case "doctors":
        navigate("/doctors");
        break;
      case "ras":
        navigate("/ras");
        break;
      case "labelers":
        navigate("/labelers");
        break;
      case "institutionowners":
        navigate("/institutionowners");
        break;
      case "admins":
        navigate("/admins");
        break;
      case "disclaimer":
        navigate("/disclaimer");
        break;
      case "contact":
        navigate("/contact");
        break;
      default:
        navigate("/dashboard");
    }
  };

  const renderCurrentScreen = () => {
    switch (activeScreen) {
      case "dashboard":
        return <DashboardHome />;
      case "video":
        return <VideoScreen />;
      case "participants":
        return <PatientsScreen />;
      case "patient-form":
        return <PatientFormScreen screenData={screenData} />;
      case "doctors":
        return <DoctorsScreen />;
      case "doctor-form":
        return <DoctorFormScreen screenData={screenData} />;
      case "ras":
        return <RAsScreen />;
      case "ra-form":
        return <RAFormScreen screenData={screenData} userProfile={userProfile} />;
      case "labelers":
        return <LabelersScreen />;
      case "labeler-form":
        return <LabelerFormScreen screenData={screenData} />;
      case "institutionowners":
        return <InstitutionOwnersScreen />;
      case "institutionowner-form":
        return <InstitutionOwnerFormScreen screenData={screenData} />;
      case "admins":
        return <AdminsScreen />;
      case "admin-form":
        return <AdminFormScreen screenData={screenData} />;
      case "disclaimer":
        return <DisclaimerScreen />;
      case "contact":
        return <ContactScreen />;
      default:
        return <DashboardHome />;
    }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-layout">
        <Sidebar
          activeMenuItem={getActiveMenuItem()}
          onMenuItemClick={handleMenuItemClick}
          userProfile={userProfile}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />
        <main className="main-content">
          <Header
            userProfile={userProfile}
            userLoading={userLoading}
            setSidebarOpen={setSidebarOpen}
          />
          <div className="content-area">
            <div className="content-wrapper">
              <div className="dashboard-content">{renderCurrentScreen()}</div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
